import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircleOutlined } from '@ant-design/icons';
import { getSystemUrlByClientId, logout } from '../../api/auth';
import './Callback.css';

const FALLBACK_MAP: Record<string, string> = {
  'zj-sso': 'https://zjsso.shjinjia.com.cn/',
  moss_prod: 'https://moss.shjinjia.com.cn/',
  app_flowy_web: 'https://appflowy.shjinjia.com.cn/',
  vinka: 'https://vinkclaw.ziggie.cn',
};

const DEFAULT_FALLBACK_URL = 'https://moss.shjinjia.com.cn/';

const clearAuthStorage = () => {
  // 只定向清理登录态相关 key，避免影响本页派发/其他业务缓存
  [
    'ReturnUrl',
    'access_token',
    'token',
    'tokenObj',
    'realName',
    'userInfo',
    'user_lid'
  ].forEach((k) => localStorage.removeItem(k));
};

const withTimeout = async <T,>(p: Promise<T>, ms: number) => {
  let t: number | undefined;
  try {
    return await Promise.race([
      p,
      new Promise<T>((_, reject) => {
        t = window.setTimeout(() => reject(new Error('timeout')), ms);
      }),
    ]);
  } finally {
    if (t) window.clearTimeout(t);
  }
};

/**
 * 登出回调页面
 *
 * 访问示例：
 * https://zjssotest.shjinjia.com.cn:10091/logout?logoutId=xxxx
 *
 * 1. 从地址栏中获取 logoutId 参数
 * 2. 调用后端 Account/Logout 接口（GET），将 logoutId 传递过去
 * 3. 无论成功或失败，最终跳转到系统首页：https://zjssotest.shjinjia.com.cn:10091/
 */
const LogoutCallback = () => {
  const location = useLocation();
  const [statusText, setStatusText] = useState('正在读取登出信息...');
  const [subStatusText, setSubStatusText] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const doLogout = async () => {
      try {
        setStatusText('正在读取登出信息...');
        setSubStatusText('正在解析登出参数');

        const searchParams = new URLSearchParams(location.search);
        const logoutId = searchParams.get('logoutId');

        const clientId =
          searchParams.get('ClientId') ||
          searchParams.get('clientId') ||
          searchParams.get('client_id') ||
          '';

        const fallbackUrl = FALLBACK_MAP[clientId] || DEFAULT_FALLBACK_URL;

        const resolveSystemUri = async () => {
          if (!clientId) return '';

          const cacheKey = `logout_dispatch_systemUri:${clientId}`;
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) return cached;

          const systemUri = (await getSystemUrlByClientId(clientId)).trim();
          if (systemUri) {
            sessionStorage.setItem(cacheKey, systemUri);
            return systemUri;
          }
          return '';
        };

        if (!logoutId) {
          console.warn('未找到 logoutId 参数，直接跳转到首页');
          setStatusText('未找到登出参数');
          setSubStatusText('正在跳转到首页...');
          setTimeout(() => {
            window.location.href = clientId ? fallbackUrl : `${window.location.origin}/`;
          }, 1000);
          return;
        }

        setStatusText('正在准备跳转目标...');
        setSubStatusText('正在匹配系统地址');
        const systemUri = await resolveSystemUri();
        const targetUrl = systemUri || fallbackUrl;

        setStatusText('正在向服务器发起登出请求...');
        setSubStatusText('请稍候');

        // 登出接口可能会清 cookie；我们已提前算好 targetUrl，不再依赖登出后的登录态
        try {
          await withTimeout(logout({ logoutId }), 8000);
        } catch (e) {
          console.warn('登出接口调用失败或超时，将继续跳转:', e);
        }

        setStatusText('正在清理本地登录态...');
        setSubStatusText('即将安全跳转');
        clearAuthStorage();

        setStatusText('登出成功');
        setSubStatusText('正在安全跳转...');
        setIsSuccess(true);

        setTimeout(() => {
          window.location.href = targetUrl;
        }, 300);
      } catch (error) {
        console.error('调用 Account/Logout 接口失败:', error);
        setStatusText('登出处理完成');
        setSubStatusText('正在跳转...');
        setIsSuccess(true);

        // 无论接口是否成功，都跳转到首页
        setTimeout(() => {
          window.location.href = `${window.location.origin}/`;
        }, 1000);
      }
    };

    doLogout();
  }, [location.search]);

  return (
    <div className="callback-page">
      <div className="callback-card">
        {isSuccess ? (
          <CheckCircleOutlined className="callback-success-icon" />
        ) : (
          <div className="callback-spinner"></div>
        )}
        <div className="callback-title">
          {isSuccess ? '登出成功' : '正在退出登录'}
        </div>
        <div className="callback-desc">
          {isSuccess 
            ? '请稍候，正在为您安全跳转...' 
            : '请稍候，正在安全退出并返回首页'}
        </div>
        <div className="callback-status">
          {statusText}
        </div>
        {subStatusText && (
          <div className="callback-sub-status">{subStatusText}</div>
        )}
        {!isSuccess && (
          <div className="callback-hint">
            如果长时间无响应，请关闭此页重新尝试。
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoutCallback;